import { useEffect, useState } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { PROVIDER_MODELS, IS_MOCK_DEMO } from '../../constants';
import BillingPanel from '../BillingPanel';
import AdminCreditPanel from '../admin/AdminCreditPanel';
import AdminModelPanel from '../admin/AdminModelPanel';
import AdminInvitedPanel from '../admin/AdminInvitedPanel';

function ModelSelector({ models, llmModel, llmModelLight, setLlmModelLight, llmModelHeavy, setLlmModelHeavy }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-0.5">会話・要約用モデル</label>
        <p className="text-[10px] text-gray-500 mb-1.5">チャット返信・Noteまとめ</p>
        <select
          value={llmModelLight || llmModel}
          onChange={(e) => setLlmModelLight(e.target.value)}
          className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-blue-500"
        >
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-0.5">抽出・統合用モデル</label>
        <p className="text-[10px] text-gray-500 mb-1.5">Hint抽出・ヘルスチェック</p>
        <select
          value={llmModelHeavy || llmModel}
          onChange={(e) => setLlmModelHeavy(e.target.value)}
          className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-blue-500"
        >
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
    </div>
  );
}

export default function SettingsModal({
  isSettingsOpen,
  setIsSettingsOpen,
  handleSaveSettings,
  user,
  signOut,
  llmProvider,
  setLlmProvider,
  llmModel,
  setLlmModel,
  llmModelLight,
  setLlmModelLight,
  llmModelHeavy,
  setLlmModelHeavy,
  openaiApiKey,
  setOpenaiApiKey,
  anthropicApiKey,
  setAnthropicApiKey,
  geminiApiKey,
  setGeminiApiKey,
  debugMode,
  setDebugMode,
  userData,
  isByok,
}) {
  const [usageEvents, setUsageEvents] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);

  useEffect(() => {
    async function fetchUsage() {
      if (!isSettingsOpen || !user?.uid || IS_MOCK_DEMO || isByok) return;
      setLoadingUsage(true);
      try {
        const q = query(
          collection(db, 'usageEvents'),
          where('ownerUid', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(500)
        );
        const snapshot = await getDocs(q);
        const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsageEvents(events);
      } catch (err) {
        console.error('Failed to fetch usage events:', err);
      } finally {
        setLoadingUsage(false);
      }
    }
    fetchUsage();
  }, [isSettingsOpen, user?.uid, isByok]);

  // Ensure model is valid for the current provider
  useEffect(() => {
    if (isSettingsOpen && llmProvider && PROVIDER_MODELS[llmProvider]) {
      if (!llmModel || !PROVIDER_MODELS[llmProvider].includes(llmModel)) {
        setLlmModel(PROVIDER_MODELS[llmProvider][0]);
      }
    }
  }, [llmProvider, isSettingsOpen, llmModel, setLlmModel]);

  const [activeTab, setActiveTab] = useState('general');
  const [adminTab, setAdminTab] = useState('credits');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsSettingsOpen(false);
      }
    };
    if (isSettingsOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSettingsOpen, setIsSettingsOpen]);


  if (!isSettingsOpen) return null;

  const providers = [
    { id: 'gemini', label: 'Gemini' },
    { id: 'openai', label: 'OpenAI' },
    { id: 'anthropic', label: 'Anthropic' },
  ];

  return (
    <div className="fixed inset-0 z-[160] bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setIsSettingsOpen(false); }}>
      <div className={`w-full ${activeTab === 'admin' ? 'max-w-4xl' : 'max-w-md'} bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]`}>
        <div className="p-4 border-b border-gray-700 flex items-center justify-between bg-gray-800">
          <div className="flex items-center gap-4">
            <h2 className="text-base font-bold text-white">設定</h2>
            {(user?.role === 'owner' || userData?.role === 'owner') && (
              <div className="flex bg-gray-900 rounded-lg p-0.5 border border-gray-700">
                <button
                  onClick={() => setActiveTab('general')}
                  className={`px-3 py-1 rounded text-xs font-bold transition-all ${activeTab === 'general' ? 'bg-gray-700 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  一般
                </button>
                <button
                  onClick={() => setActiveTab('admin')}
                  className={`px-3 py-1 rounded text-xs font-bold transition-all ${activeTab === 'admin' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  Admin
                </button>
              </div>
            )}
          </div>
          <button aria-label="閉じる" type="button" onClick={() => setIsSettingsOpen(false)} className="text-gray-400 hover:text-white text-xl leading-none">
            ×
          </button>
        </div>

        {activeTab === 'admin' && (user?.role === 'owner' || userData?.role === 'owner') ? (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-700 bg-gray-800 flex items-center gap-2">
              <button
                onClick={() => setAdminTab('credits')}
                className={`px-3 py-1 rounded text-xs font-bold transition-all ${adminTab === 'credits' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}
              >
                Credits
              </button>
              <button
                onClick={() => setAdminTab('models')}
                className={`px-3 py-1 rounded text-xs font-bold transition-all ${adminTab === 'models' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}
              >
                Models
              </button>
              <button
                onClick={() => setAdminTab('invited')}
                className={`px-3 py-1 rounded text-xs font-bold transition-all ${adminTab === 'invited' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-400 hover:text-gray-200'}`}
              >
                Invited
              </button>
            </div>
            {adminTab === 'credits' ? (
              <AdminCreditPanel user={user} />
            ) : adminTab === 'models' ? (
              <AdminModelPanel user={user} />
            ) : (
              <AdminInvitedPanel user={user} />
            )}
            
            {/* Admin Debug Tools */}
            <div className="p-4 border-t border-gray-700 bg-gray-900/50">
              <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-2">Admin Debug Tools</div>
              <button
                type="button"
                onClick={() => {
                  throw new Error("Sentry Admin Test - " + new Date().toISOString());
                }}
                className="w-full px-3 py-2 rounded text-[10px] font-bold transition-all bg-rose-900/20 text-rose-400 border border-rose-800/50 hover:bg-rose-900/40"
              >
                ⚠ Break the world (Sentry Test)
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSaveSettings} className="flex-1 flex flex-col min-h-0 overflow-hidden">

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {user && (
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-3">
              <div className="text-xs text-gray-400 mb-1">アカウント</div>
              <div className="text-sm font-medium text-white mb-2">{user.email}</div>
              <div className="flex justify-between items-center">
                <div className="text-xs px-2 py-0.5 rounded bg-blue-900/50 text-blue-300 border border-blue-700">
                  {user.role === 'owner' ? 'Owner' : user.role === 'member' ? 'Member' : 'Viewer'}
                </div>
                {signOut && (
                  <button type="button" onClick={signOut} className="text-xs text-rose-400 hover:text-rose-300 px-2 py-1 bg-rose-900/20 rounded border border-rose-800/50">
                    サインアウト
                  </button>
                )}
              </div>
            </div>
          )}

          {!IS_MOCK_DEMO && (
            <BillingPanel
              user={user}
              isActive={true}
              userData={userData}
              usageEvents={usageEvents}
              loadingUsage={loadingUsage}
            />
          )}

          <div className="space-y-4">
            <div className="bg-blue-950/40 border border-blue-700/40 rounded-lg p-3 text-[11px] leading-relaxed text-blue-200">
              <p>⚡ APIキーを設定しない場合、システム標準AI（Sink提供の共通キー）を使用します。この場合、AI機能を使うたびに <strong className="text-blue-100">Sink クレジット</strong> を消費し、混雑時には制限がかかる場合があります。</p>
              <p className="mt-1">🔑 <strong>自分のAPIキーを使う：</strong> OpenAIやGeminiのAPIキーを設定すると、Sink クレジットを消費せずにAIを利用できます。</p>
              <p className="mt-1">🔒 <strong>安心してください：</strong> 入力したAPIキーはSinkのサーバーには送信されず、あなたのブラウザにのみ安全に保存されます。</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">AI プロバイダー</label>
              <div className="flex p-1 bg-gray-900 rounded-lg border border-gray-700">
                {providers.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setLlmProvider(p.id)}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                      llmProvider === p.id 
                        ? 'bg-blue-600 text-white shadow-sm' 
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gray-700/30 border border-gray-700 rounded-lg p-4 space-y-4">
              {llmProvider === 'gemini' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                  <ModelSelector
                    models={PROVIDER_MODELS.gemini}
                    llmModel={llmModel} setLlmModel={setLlmModel}
                    llmModelLight={llmModelLight} setLlmModelLight={setLlmModelLight}
                    llmModelHeavy={llmModelHeavy} setLlmModelHeavy={setLlmModelHeavy}

                  />
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Gemini APIキー</label>
                    <input
                      type="password"
                      value={geminiApiKey}
                      onChange={(e) => setGeminiApiKey(e.target.value)}
                      placeholder="Google AI StudioのAPIキー"
                      className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {llmProvider === 'openai' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                  <ModelSelector
                    models={PROVIDER_MODELS.openai}
                    llmModel={llmModel} setLlmModel={setLlmModel}
                    llmModelLight={llmModelLight} setLlmModelLight={setLlmModelLight}
                    llmModelHeavy={llmModelHeavy} setLlmModelHeavy={setLlmModelHeavy}

                  />
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">OpenAI APIキー</label>
                    <input
                      type="password"
                      value={openaiApiKey}
                      onChange={(e) => setOpenaiApiKey(e.target.value)}
                      placeholder="sk-..."
                      className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {llmProvider === 'anthropic' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                  <ModelSelector
                    models={PROVIDER_MODELS.anthropic}
                    llmModel={llmModel} setLlmModel={setLlmModel}
                    llmModelLight={llmModelLight} setLlmModelLight={setLlmModelLight}
                    llmModelHeavy={llmModelHeavy} setLlmModelHeavy={setLlmModelHeavy}

                  />
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Anthropic APIキー</label>
                    <input
                      type="password"
                      value={anthropicApiKey}
                      onChange={(e) => setAnthropicApiKey(e.target.value)}
                      placeholder="sk-ant-..."
                      className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">デバッグモード</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">エラーの詳細情報を常に表示します。</div>
                </div>
                <button
                  type="button"
                  onClick={() => setDebugMode(!debugMode)}
                  className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${debugMode ? 'bg-indigo-600' : 'bg-gray-700'}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${debugMode ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 space-y-2">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">リーガル</div>
              <div className="flex flex-col gap-2">
                <a href="/privacy" className="text-xs text-indigo-400 hover:text-indigo-300 py-1 transition-colors flex justify-between items-center">
                  <span>プライバシーポリシー</span>
                  <span className="text-[10px] opacity-50">→</span>
                </a>
                <a href="/terms" className="text-xs text-indigo-400 hover:text-indigo-300 py-1 transition-colors flex justify-between items-center">
                  <span>利用規約</span>
                  <span className="text-[10px] opacity-50">→</span>
                </a>
                <a href="/tokushoho" className="text-xs text-indigo-400 hover:text-indigo-300 py-1 transition-colors flex justify-between items-center">
                  <span>特定商取引法に基づく表記</span>
                  <span className="text-[10px] opacity-50">→</span>
                </a>
              </div>
            </div>
          </div>

        </div>

            <div className="p-4 border-t border-gray-700 bg-gray-800">
              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold py-2.5 rounded-lg transition-colors shadow-lg active:scale-[0.98]">
                設定を保存
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
